import {
  LocationSelectItem,
  LocationVariantType,
} from '@component/Locations/LocationSelectItem';
import {
  AsteriskOutlinedIcon,
  CheckCircleOutlineIcon,
  ConfirmSignIcon,
} from '@src/svg';
import InfoTooltip from '@uikit/Info/InfoTooltip/InfoTooltip';
import LocationModalWrapper from '@uikit/FormItems/LocationInput/LocationModalWrapper';
import { getLocationAsText, placeAddressInfo } from '@src/utils';
import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import Tooltip from '@mui/material/Tooltip';
import { usePlacesSearch } from '@hooks/usePlaceSearch';
import {
  LocationInputPropsType,
  OverrideType,
} from '@uikit/FormItems/constants';
import { useOutsideClick } from '@hooks/useOutsideClick';
import { useLazyGetPlacesLocationTypesQuery } from '@store/rtk/selects';
import { useLazyCheckPhoneNumberQuery } from '@store/rtk/twilio';
import { useTextTruncation } from '@hooks/useTextTruncation';
import { TooltipWrapper } from '@uikit/TooltipWrapper';

const VERIFIED_ADDRESS_TOOLTIP = 'Verified Address';
const isVerifiedAddress = (verifiedAddress?: boolean, verified?: boolean) =>
  Boolean(verifiedAddress ?? verified);

const LocationInput = forwardRef<HTMLInputElement, LocationInputPropsType>(
  (
    {
      maxWidth,
      defaultValue,
      inputId,
      disabled,
      title,
      required = false,
      withIcon = true,
      info,
      IconLeft,
      name,
      placeholder,
      change,
      onBlur,
      verified,
      verifiedAddress: backendVerifiedAddress,
      isInModal = false,
      setIsModalOpened,
      dropdownClassName,
      onPlaceSelect,
      index,
      error,
      validationDynamicClassName,
      tabIndex,
      dataTestId,
    },
    ref
  ) => {
    const [getPlacesLocationTypes] = useLazyGetPlacesLocationTypesQuery();
    const [triggerPhoneLookup] = useLazyCheckPhoneNumberQuery();
    const [toggleLocationModal, setToggleLocationModal] = useState<{
      toggle: boolean;
      address: OverrideType | null;
    }>({ toggle: false, address: null });
    const [verifiedAddress, setVerifiedAddress] = useState<boolean>(
      isVerifiedAddress(backendVerifiedAddress, verified)
    );
    const [focused, setFocused] = useState<boolean>(false);
    const [savedValue, setSavedValue] = useState<string>(defaultValue);
    const [valueHasSaved, setValueHasSaved] = useState<boolean>(false);
    const { tooltipContent, bindTruncation } = useTextTruncation();

    const dropdownRef = useRef<HTMLDivElement>(null);
    const {
      suggestions,
      handleSearchChange,
      setSearchQuery,
      searchQuery,
      memberPlaces,
      setToggle,
      toggle,
    } = usePlacesSearch(defaultValue);

    useEffect(() => {
      setVerifiedAddress(isVerifiedAddress(backendVerifiedAddress, verified));
    }, [backendVerifiedAddress, verified]);

    const handleLocationInputChange = (
      e: React.ChangeEvent<HTMLInputElement>
    ) => {
      setVerifiedAddress(false);
      handleSearchChange(e);
    };

    useOutsideClick({
      ref: dropdownRef,
      onOutsideClick: () => {
        setFocused(false);
        setToggle(false);

        if (!searchQuery?.trim() || searchQuery === defaultValue) {
          setSearchQuery(valueHasSaved ? savedValue : defaultValue);
        }
      },
    });

    useEffect(() => {
      setSearchQuery(valueHasSaved ? savedValue : defaultValue);
    }, [valueHasSaved, savedValue]);

    useEffect(() => {
      setIsModalOpened && setIsModalOpened(toggleLocationModal.toggle);
    }, [toggleLocationModal]);

    const handleAddressSelection = async (selectedPlace: any) => {
      const types = await getPlacesLocationTypes({}).unwrap();
      const { Place } = (await google.maps.importLibrary(
        'places'
      )) as google.maps.PlacesLibrary;

      const location = new Place({
        id: selectedPlace.id,
        requestedLanguage: 'en',
      });

      const { place } = JSON.parse(
        JSON.stringify(
          await location.fetchFields({
            fields: [
              'addressComponents',
              'displayName',
              'location',
              'nationalPhoneNumber',
              'regularOpeningHours',
              'formattedAddress',
              'googleMapsURI',
              'types',
              'svgIconMaskURI',
              'primaryType',
            ],
          })
        )
      );
      if (place) {
        const returnExactTypeObject = ({
          type,
          defaultType,
        }: {
          type: string;
          defaultType?: string;
        }) => {
          return (
            types.find((t) => t.value === type) ??
            typesObjectInstance(defaultType as string)
          );
        };
        const typesObjectInstance = (type: string) => {
          const copiedType = type;
          const newType =
            copiedType.substring(0, 1).toUpperCase() +
            copiedType.substring(1).replaceAll('_', ' ');
          return {
            id: 0,
            label: newType,
            value: type,
          };
        };

        const convertedType =
          returnExactTypeObject({
            type: place.primaryType ? place.primaryType : place.types[0],
            defaultType: place.types[0],
          }) || typesObjectInstance(place.types[0]);

        const clonedPlace = { ...place, type: convertedType };
        let newPlace: OverrideType = {
          ...placeAddressInfo(clonedPlace),
        };
        if (newPlace.phoneNumber && newPlace.phoneNumber.length === 10) {
          const mobileTypeCheck = await triggerPhoneLookup(
            newPlace.phoneNumber
          ).unwrap();
          newPlace = {
            ...newPlace,
            phoneType: mobileTypeCheck?.data?.lineTypeIntelligence?.type,
          };
        }
        if (isInModal) {
          const text = getLocationAsText(newPlace);
          setSearchQuery(text);
          setSavedValue(text);
          setValueHasSaved(true);
          setVerifiedAddress(false);
          change && change(newPlace);
        } else {
          setVerifiedAddress(false);
          onPlaceSelect && onPlaceSelect(newPlace, index);
          setToggleLocationModal({
            toggle: true,
            address: newPlace,
          });
        }
      }
    };

    const handleSelectVerifiedAddress = async (
      e,
      selected: any,
      whichLocation?: LocationVariantType
    ) => {
      const recombinedPlace = {
        ...selected,
        verifiedAddress: isVerifiedAddress(
          selected.verifiedAddress,
          selected.verified
        ),
        verified: true,
        phoneNumber: selected.phoneNumber ?? '',
        phoneType: selected.phoneType ?? '',
        businessName: selected.businessName ?? '',
        apiFrom: 'storage',
      };

      e.stopPropagation();
      setVerifiedAddress(recombinedPlace.verifiedAddress);
      if (isInModal) {
        // TODO check this and then approve
        const text = getLocationAsText(selected);
        setSearchQuery(text);
        setSavedValue(text);
        setValueHasSaved(true);
        change && change(recombinedPlace);
      } else {
        onPlaceSelect && onPlaceSelect(recombinedPlace, index);
        setToggleLocationModal({
          toggle: true,
          address: recombinedPlace,
        });
      }
      setToggle(false);
    };

    const locationModalWrapperProps = useMemo(() => {
      return {
        index,
        isInModal,
        toggleLocationModal,
        searchInputValue: searchQuery,
        dataTestId: `${dataTestId}-wrapper`,
        change,
        setSavedValue,
        setValueHasSaved,
        setToggleLocationModal,
        setVerifiedAddress,
      };
    }, [
      // change,
      // searchQuery
      index,
      isInModal,
      dataTestId,
      toggleLocationModal,
      setSavedValue,
      setValueHasSaved,
      setToggleLocationModal,
      setVerifiedAddress,
    ]);

    return (
      <>
        <LocationModalWrapper {...locationModalWrapperProps} />
        <div
          className="location-select"
          style={{
            zIndex: toggle ? '20' : '0',
          }}
          id={inputId}
          ref={dropdownRef}
        >
          <div style={{ maxWidth: maxWidth }} className="input-text">
            {info?.toggle && (
              <InfoTooltip className="info_shower" title={info?.desc}>
                <div></div>
              </InfoTooltip>
            )}
            <div
              className={!disabled ? '' : 'disabled location-input-disabled'}
            >
              <div className="input-title-wrapper">
                {title && (
                  <span className="input-title">
                    {title}
                    {required && (
                      <span
                        className="input-required-marker"
                        aria-hidden="true"
                      >
                        <AsteriskOutlinedIcon
                          width={10}
                          height={10}
                          color="#fb6403"
                        />
                      </span>
                    )}
                  </span>
                )}
              </div>
              <TooltipWrapper
                tooltip={tooltipContent}
                open={Boolean(tooltipContent)}
              >
                <span
                  className={`input ${validationDynamicClassName} ${
                    error ? 'error' : ''
                  }`}
                  {...bindTruncation}
                >
                  {IconLeft && typeof IconLeft === 'function' && <IconLeft />}
                  <input
                    data-test-id={dataTestId}
                    id={inputId}
                    ref={ref}
                    type="text"
                    className="truncate-input google_input"
                    name={name}
                    disabled={disabled}
                    aria-required={required || undefined}
                    value={searchQuery}
                    placeholder={placeholder}
                    {...(tabIndex ? { tabIndex } : {})}
                    tabIndex={tabIndex}
                    onKeyDown={(e) => {
                      if (e.key === 'Tab') {
                        setFocused(false);
                        setToggle(false);
                        setSearchQuery(savedValue ? savedValue : searchQuery);
                      }
                    }}
                    onFocus={(e) => {
                      // setFocused(true);
                      handleSearchChange(e);
                    }}
                    onChange={handleLocationInputChange}
                    onBlur={(e) => {
                      onBlur && onBlur(e);
                    }}
                  />
                  {verifiedAddress && (
                    <Tooltip title={VERIFIED_ADDRESS_TOOLTIP} placement="top">
                      <span
                        className="verified-address-icon"
                        aria-label={VERIFIED_ADDRESS_TOOLTIP}
                        data-test-id={
                          dataTestId
                            ? `${dataTestId}-verified-address`
                            : undefined
                        }
                      >
                        {isInModal ? (
                          <CheckCircleOutlineIcon
                            width={20}
                            height={20}
                            color="#32936F"
                          />
                        ) : (
                          <ConfirmSignIcon
                            width={16}
                            height={16}
                            color="#32936F"
                          />
                        )}
                      </span>
                    </Tooltip>
                  )}
                </span>
              </TooltipWrapper>
            </div>
          </div>
          {/*{toggle && focused && (*/}
          {toggle && (
            <div
              className={`location-select__body ${dropdownClassName || ''}`}
              onClick={(e) => e.stopPropagation()}
            >
              <LocationSelectItem
                dataTestId={`${dataTestId}-option`}
                title="Address Results"
                handleSelectVerifiedAddress={handleSelectVerifiedAddress}
                handleAddressSelection={handleAddressSelection}
                data={[...memberPlaces, ...suggestions]}
                hasIcon={false}
                whichLocation="member"
                isInModal={isInModal}
                setToggleLocationModal={setToggleLocationModal}
              />
            </div>
          )}
        </div>
      </>
    );
  }
);

export default LocationInput;
